import { useState, useEffect, useRef, useCallback } from "react";
import { useApp, api } from "../../context";
import { Shell } from "../../components";

// ── Sound Engine (Web Audio API) ──
const createSound = () => {
  if (typeof window === "undefined") return null;
  try { return new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
};

const playSound = (type) => {
  const ctx = createSound();
  if (!ctx) return;
  const g = ctx.createGain();
  g.connect(ctx.destination);

  const o = ctx.createOscillator();
  o.connect(g);

  const now = ctx.currentTime;

  switch(type) {
    case "pick":
      o.frequency.setValueAtTime(520, now);
      o.frequency.exponentialRampToValueAtTime(640, now + 0.06);
      g.gain.setValueAtTime(0.18, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      o.start(now); o.stop(now + 0.08);
      break;
    case "place":
      o.type = "sine";
      o.frequency.setValueAtTime(300, now);
      o.frequency.exponentialRampToValueAtTime(180, now + 0.12);
      g.gain.setValueAtTime(0.22, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
      o.start(now); o.stop(now + 0.14);
      break;
    case "win":
      [0, 0.12, 0.24, 0.38].forEach((t, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = [523, 659, 784, 1047][i];
        gain.gain.setValueAtTime(0.28, now + t);
        gain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.25);
        osc.start(now + t); osc.stop(now + t + 0.25);
      });
      break;
    case "lose":
      o.type = "sawtooth";
      o.frequency.setValueAtTime(400, now);
      o.frequency.exponentialRampToValueAtTime(150, now + 0.5);
      g.gain.setValueAtTime(0.2, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      o.start(now); o.stop(now + 0.5);
      break;
    case "tick":
      o.type = "square";
      o.frequency.setValueAtTime(880, now);
      g.gain.setValueAtTime(0.08, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      o.start(now); o.stop(now + 0.05);
      break;
    case "refill":
      [0, 0.1, 0.2].forEach((t) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 440 + t * 1000;
        gain.gain.setValueAtTime(0.15, now + t);
        gain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.1);
        osc.start(now + t); osc.stop(now + t + 0.1);
      });
      break;
    default: break;
  }
};

const getCAP = (level) => {
  if (level <= 10) return 4;
  if (level <= 25) return 5;
  if (level <= 40) return 6;
  return 7;
};   

const PALETTE = [
  ["#6fc7ff", "#1473e6"],
  ["#ff8a8a", "#d31222"],
  ["#8aff8a", "#15a31f"],
  ["#fff3a0", "#e6c411"],
  ["#ffc07d", "#e06400"],
  ["#ffb1d8", "#e8559b"],
  ["#c9a7ff", "#6a2fd6"],
  ["#7df3e1", "#0e9c8a"],
];

function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateTubes(numColors, seed, cap = 4) {
  const rng = mulberry32(seed);
  const balls = [];
  for (let c = 0; c < numColors; c++) for (let i = 0; i < cap; i++) balls.push(c);
  for (let i = balls.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [balls[i], balls[j]] = [balls[j], balls[i]];
  }
  const tubes = [];
  for (let t = 0; t < numColors; t++) tubes.push(balls.slice(t * cap, (t + 1) * cap));
  tubes.push([], []);
  const solved = tubes.every(t => t.length === 0 || (t.length === cap && t.every(b => b === t[0])));
  return solved ? generateTubes(numColors, seed + 1, cap) : tubes;
}

const isTubeComplete = (t, cap = 4) => t.length === cap && t.every(b => b === t[0]);
const isBoardSolved = (tubes, cap = 4) => tubes.every(t => t.length === 0 || isTubeComplete(t, cap));

function Confetti() {
  const pieces = Array.from({ length: 48 }, (_, i) => i);
  const striped = (i) => 7 + (i % 13);
  return (
    <div className="bs-confetti" aria-hidden="true">
      {pieces.map((i) => {
        const [hi] = PALETTE[i % PALETTE.length];
        return (
          <i key={i} style={{
            left: `${(i * striped(i)) % 100}%`,
            background: i % 5 === 0 ? "#ffd34d" : hi,
            animationDelay: `${(i % 12) * 0.12}s`,
            animationDuration: `${2.2 + (i % 7) * 0.25}s`,
            width: i % 3 === 0 ? "6px" : "9px",
            height: i % 4 === 0 ? "14px" : "9px",
            borderRadius: i % 2 ? "50%" : "2px",
          }} />
        );
      })}
    </div>
  );
}

const LEVEL_CONFIG = (level) => {
  const cap = getCAP(level);
  if (level <= 5)  return { colors: 3, time: 90,  gt: 1,  cap };
  if (level <= 10) return { colors: 4, time: 75,  gt: 2,  cap };
  if (level <= 20) return { colors: 5, time: 60,  gt: 3,  cap };
  if (level <= 35) return { colors: 6, time: 75,  gt: 4,  cap };
  if (level <= 50) return { colors: 7, time: 60,  gt: 6,  cap };
  return                  { colors: 8, time: 45,  gt: 10, cap };
};

function RefillCountdown({ refillAt }) {
  const [timeStr, setTimeStr] = useState("");
  useEffect(() => {
    const update = () => {
      const diff = new Date(refillAt) - new Date();
      if (diff <= 0) { setTimeStr("Ready!"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeStr(`${h}h ${m}m ${s}s`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [refillAt]);
  return <span>{timeStr}</span>;
}

export default function BallSortPage() {
  const { go, flash, user, setUser } = useApp();
  const [gameState, setGameState] = useState(null);
  const [tubes, setTubes] = useState([]);
  const [selected, setSelected] = useState(-1);
  const [moves, setMoves] = useState(0);
  const [timeLeft, setTimeLeft] = useState(90);
  const [phase, setPhase] = useState("loading");
  const [gtResult, setGtResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbPage, setLbPage] = useState(1);
  const [lbTotalPages, setLbTotalPages] = useState(1);
  const [myRank, setMyRank] = useState(null);
  const [beatenPlayer, setBeatenPlayer] = useState(null);
  const [lbLoading, setLbLoading] = useState(false);
  const [lastDrop, setLastDrop] = useState(null);
  const historyRef = useRef([]);

  const loadLeaderboard = useCallback((page = 1) => {
    setLbLoading(true);
    api(`/solo/leaderboard?page=${page}`).then(r => {
      if (r.ok) {
        setLeaderboard(r.data.leaderboard || []);
        setMyRank(r.data.my_rank);
        setBeatenPlayer(r.data.beaten_player);
        setLbTotalPages(r.data.total_pages || 1);
        setLbPage(page);
      }
      setLbLoading(false);
    });
  }, []);

  const deal = useCallback((level, config) => {
    const cfg = config || LEVEL_CONFIG(level);
    const cap = cfg.cap || getCAP(level);
    const seed = (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0;
    setTubes(generateTubes(cfg.colors, seed, cap));
    setSelected(-1);
    setMoves(0);
    setTimeLeft(cfg.time);
    setGtResult(null);
    setLastDrop(null);
    historyRef.current = [];
    setPhase("playing");
  }, []);

  useEffect(() => {
    api("/solo/state").then(r => {
      if (r.ok) {
        setGameState(r.data);
        if (r.data.lives <= 0) setPhase("no_lives");
        else deal(r.data.level, r.data.config || LEVEL_CONFIG(r.data.level));
        loadLeaderboard(1);
      }
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (phase !== "playing") return;
    if (timeLeft <= 0) { handleLost(); return; }
    if (timeLeft <= 10) playSound("tick");
    const id = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, timeLeft]);

  const handleTap = (idx) => {
    if (phase !== "playing") return;
    setLastDrop(null);
    if (selected === -1) {
      if (tubes[idx].length === 0) return;
      playSound("pick");
      setSelected(idx); return;
    }
    if (selected === idx) { setSelected(-1); return; }
    const src = tubes[selected];
    const dst = tubes[idx];
    const cap = config.cap || getCAP(gameState?.level || 1);
const legal = src.length > 0 && dst.length < cap;
    if (!legal) { setSelected(tubes[idx].length ? idx : -1); return; }
    historyRef.current.push(tubes.map(t => t.slice()));
    const next = tubes.map(t => t.slice());
    next[idx].push(next[selected].pop());
    setTubes(next);
    setSelected(-1);
    setMoves(m => m + 1);
    playSound("place");
    setLastDrop({ tube: idx });
    if (isBoardSolved(next, config.cap || getCAP(gameState?.level || 1))) handleWon(next);
  };

  const handleUndo = () => {
    if (phase !== "playing" || !historyRef.current.length) return;
    setTubes(historyRef.current.pop());
    setSelected(-1); setLastDrop(null);
    setMoves(m => m + 1);
  };

  const handleWon = async (finalTubes) => {
    setPhase("won");
    playSound("win");
    const res = await api("/solo/complete", {
      method: "POST",
      body: JSON.stringify({ level: gameState?.level || 1, moves, time_left: timeLeft, won: true }),
    });
    if (res.ok) {
      setGtResult(res.data);
      setGameState(prev => ({ ...prev, level: res.data.new_level, lives: res.data.lives, streak: res.data.streak }));
      if (res.data.gt_earned > 0 && user) setUser(u => ({ ...u, gtBalance: (u.gtBalance || 0) + res.data.gt_earned }));
      loadLeaderboard(1);
    }
  };

  const handleLost = async () => {
    setPhase("lost");
    playSound("lose");
    const res = await api("/solo/complete", {
      method: "POST",
      body: JSON.stringify({ level: gameState?.level || 1, moves, time_left: 0, won: false }),
    });
    if (res.ok) {
      setGtResult(res.data);
      setGameState(prev => ({ ...prev, lives: res.data.lives, streak: 0 }));
      if (res.data.lives <= 0) setPhase("no_lives");
    }
  };

  const handleNextLevel = () => {
    const newLevel = gtResult?.new_level || (gameState?.level || 1) + 1;
    deal(newLevel, LEVEL_CONFIG(newLevel));
  };

  const handleRetry = () => {
    if (!gameState || gameState.lives <= 0) { setPhase("no_lives"); return; }
    setGtResult(null);
    deal(gameState.level, LEVEL_CONFIG(gameState.level));
  };

  const handleRefillLives = async () => {
    const res = await api("/solo/refill-lives", { method: "POST" });
    if (res.ok) {
      flash("Lives refilled! ❤️❤️❤️");
      playSound("refill");
      setGameState(prev => ({ ...prev, lives: 3, life_refill_at: null }));
      // Use GT balance from server response — always accurate
      if (user) setUser(u => ({ ...u, gtBalance: res.data.gt_balance }));
      deal(gameState?.level || 1, LEVEL_CONFIG(gameState?.level || 1));
    } else flash(res.data?.message || "Failed", "error");
  };

  const config = LEVEL_CONFIG(gameState?.level || 1);
  const timeRatio = Math.max(0, Math.min(1, timeLeft / (config.time || 1)));
  const lowTime = timeRatio <= 0.25;
  const lives = gameState?.lives || 0;

  if (phase === "loading") return (
    <div className="bs-stage"><style>{CSS_TEXT}</style>
      <div className="bs-shell" style={{ alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ color: "#8d92a3", fontSize: 14 }}>Loading...</div>
      </div>
    </div>
  );

  return (
    <div className="bs-stage">
      <style>{CSS_TEXT}</style>
      <div className="bs-shell">

        {/* Back button */}
        <button onClick={() => go("solo_play")} style={{ alignSelf: "flex-start", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, color: "#f2f3f7", padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 4 }}>← Back</button>

        {/* Top bar */}
        <header className="bs-top">
          <div className="bs-lives">
            {[0,1,2].map(i => <span key={i} className={"bs-heart" + (i < lives ? "" : " off")}>❤️</span>)}
          </div>
          <div className="bs-level">
            <span className="bs-level-eyebrow">LEVEL</span>
            <span className="bs-level-num">{gameState?.level || 1}</span>
          </div>
          <div className={"bs-clock" + (lowTime ? " low" : "")}>⏱ {Math.max(0, timeLeft)}s</div>
        </header>

        {/* Timer bar */}
        <div className="bs-timer">
          <i className={lowTime ? "low" : ""} style={{ width: `${timeRatio * 100}%` }} />
        </div>

        {/* Board */}
        <main className="bs-board">
          {tubes.map((tube, ti) => {
            const complete = isTubeComplete(tube, config.cap || getCAP(gameState?.level || 1));
            const sel = ti === selected;
            return (
              <button key={ti} className={"bs-tube" + (sel ? " sel" : "") + (complete ? " done" : "")}
                style={{ '--cap': config.cap || getCAP(gameState?.level || 1) }}
                onClick={() => phase === "playing" && handleTap(ti)}>
                <span className="bs-rim" />
                {tube.map((color, bi) => {
                  const [hi, lo] = PALETTE[color % PALETTE.length];
                  const lifted = sel && bi === tube.length - 1;
                  const dropping = lastDrop?.tube === ti && bi === tube.length - 1;
                  return (
                    <span key={bi} className={"bs-ball" + (lifted ? " lift" : "") + (dropping ? " drop" : "")}
                      style={{ background: `radial-gradient(circle at 32% 28%, ${hi}, ${lo} 72%)` }}>
                    </span>
                  );
                })}
              </button>
            );
          })}
        </main>

        {/* Bottom bar */}
        <footer className="bs-bottom">
          <button className="bs-undo" onClick={handleUndo} disabled={phase !== "playing"}>↩ Undo</button>
          <div className="bs-meta">
            <span className="bs-moves">Moves {moves}</span>
            <span className="bs-gt"><span className="bs-gt-coin">GT</span> +{config.gt} on clear</span>
          </div>
        </footer>

        {/* Leaderboard */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#ffd34d", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            🏆 Ball Sort Leaderboard
            {myRank && <span style={{ fontSize: 11, color: "#8d92a3", fontWeight: 600 }}>You're #{myRank}</span>}
          </div>

          {/* My rank pinned */}
          {myRank && lbPage > 1 && (
            <div style={{ background: "rgba(255,211,77,0.12)", border: "1px solid rgba(255,211,77,0.3)", borderRadius: 12, padding: "10px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#ffd34d", minWidth: 28 }}>#{myRank}</span>
              <span style={{ fontSize: 13, color: "#ffd34d", fontWeight: 700 }}>You</span>
            </div>
          )}

          {lbLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#8d92a3", fontSize: 13 }}>Loading...</div>
          ) : leaderboard.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#8d92a3", fontSize: 13 }}>No players yet — be the first!</div>
          ) : leaderboard.map((player, i) => {
            const isMe = player.id === user?.id;
            const rank = player.rank;
            const crown = rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
            return (
              <div key={player.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, marginBottom: 6, background: isMe ? "rgba(255,211,77,0.12)" : "rgba(255,255,255,0.03)", border: `1px solid ${isMe ? "rgba(255,211,77,0.3)" : "rgba(255,255,255,0.06)"}` }}>
                <span style={{ fontSize: crown ? 18 : 13, fontWeight: 800, color: isMe ? "#ffd34d" : "#8d92a3", minWidth: 28, textAlign: "center" }}>
                  {crown || (rank ? `#${rank}` : `#${i + 1}`)}
                </span>
                <div style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, overflow: "hidden", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                  {player.avatar_url
                    ? <img src={player.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : (player.avatar_emoji || player.name?.[0] || "?")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isMe ? "#ffd34d" : "#f2f3f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isMe ? "You" : player.name}
                    {player.is_verified && <span style={{ fontSize: 10, color: "#3b82f6", marginLeft: 4 }}>✓</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "#8d92a3" }}>
                    Level {player.best_level} · 🔥{player.streak} streak
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {lbTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <button onClick={() => loadLeaderboard(lbPage - 1)} disabled={lbPage <= 1}
                style={{ padding: "8px 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: lbPage <= 1 ? "#555" : "#f2f3f7", fontSize: 12, fontWeight: 700, cursor: lbPage <= 1 ? "default" : "pointer", fontFamily: "inherit" }}>← Prev</button>
              <span style={{ padding: "8px 12px", fontSize: 12, color: "#8d92a3", fontWeight: 700 }}>
                {lbPage} / {lbTotalPages}
              </span>
              <button onClick={() => loadLeaderboard(lbPage + 1)} disabled={lbPage >= lbTotalPages}
                style={{ padding: "8px 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: lbPage >= lbTotalPages ? "#555" : "#f2f3f7", fontSize: 12, fontWeight: 700, cursor: lbPage >= lbTotalPages ? "default" : "pointer", fontFamily: "inherit" }}>Next →</button>
            </div>
          )}
        <div style={{ height: 80 }} />
        </div>

      </div>

      {/* WIN OVERLAY */}
      {phase === "won" && (
        <div className="bs-overlay">
          <Confetti />
          <div className="bs-card win">
            <div className="bs-card-glow" />
            <p className="bs-card-eyebrow">LEVEL {gameState?.level || 1} CLEARED</p>
            <h2 className="bs-card-title gold">Brilliant!</h2>
            <div className="bs-gt-burst">
              <span className="bs-gt-coin big">GT</span>
              <span className="bs-gt-amount">+{gtResult?.gt_earned ?? config.gt}</span>
            </div>
            {gtResult && (
              <p className="bs-card-sub">
                🔥 Streak ×{gtResult.streak} · Next: Level {gtResult.new_level}
                {myRank && <><br/>🏆 You're now ranked <strong style={{color:"#ffd34d"}}>#{myRank}</strong>!</>}
                {beatenPlayer && <><br/>💪 You passed <strong style={{color:"#ffd34d"}}>{beatenPlayer}</strong>!</>}
              </p>
            )}
            <div className="bs-card-row">
              <button className="bs-btn ghost" onClick={() => go("solo_play")}>Menu</button>
              <button className="bs-btn gold" onClick={handleNextLevel}>Next Level →</button>
            </div>
          </div>
        </div>
      )}

      {/* LOST OVERLAY */}
      {phase === "lost" && (
        <div className="bs-overlay red">
          <div className="bs-card lose">
            <p className="bs-card-eyebrow red">TIME'S UP</p>
            <h2 className="bs-card-title">So close!</h2>
            <p className="bs-card-sub">{lives} {lives === 1 ? "life" : "lives"} remaining.</p>
            <div className="bs-card-row">
              <button className="bs-btn ghost" onClick={() => go("solo_play")}>Menu</button>
              <button className="bs-btn red" onClick={handleRetry}>Retry level</button>
            </div>
          </div>
        </div>
      )}

      {/* NO LIVES OVERLAY */}
      {phase === "no_lives" && (
        <div className="bs-overlay red">
          <div className="bs-card lose">
            <p className="bs-card-eyebrow red">OUT OF LIVES</p>
            <h2 className="bs-card-title">💔 No lives left</h2>
            {gameState?.life_refill_at && (
              <p className="bs-card-sub">
                ⏱ Free refill in: <strong style={{ color: "#ffd34d" }}>
                  <RefillCountdown refillAt={gameState.life_refill_at} />
                </strong>
              </p>
            )}
            <p className="bs-card-sub" style={{ marginTop: 4 }}>
              Or spend <strong style={{ color: "#ffd34d" }}>5 GT</strong> to refill now.{" "}
              <span style={{ color: "#8d92a3" }}>You have {user?.gtBalance || 0} GT</span>
            </p>
            <div className="bs-card-row">
              <button className="bs-btn ghost" onClick={() => go("solo_play")}>Menu</button>
              <button className="bs-btn red" onClick={handleRefillLives}
                disabled={(user?.gtBalance || 0) < 5}
                style={{ opacity: (user?.gtBalance || 0) < 5 ? 0.5 : 1 }}>
                ❤️ Refill for 5 GT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS_TEXT = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,800&display=swap');
.bs-stage{min-height:100vh;width:100%;background:radial-gradient(900px 500px at 50% -10%,rgba(60,80,160,.18),transparent 60%),radial-gradient(700px 420px at 80% 110%,rgba(160,60,140,.10),transparent 60%),#0b0c10;display:flex;align-items:flex-start;justify-content:center;font-family:'DM Sans','SF Pro Rounded',ui-rounded,'Segoe UI',system-ui,sans-serif;color:#f2f3f7;-webkit-tap-highlight-color:transparent;user-select:none;padding:14px 10px 30px;}
.bs-shell{width:100%;max-width:480px;display:flex;flex-direction:column;gap:12px}
.bs-top{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:8px}
.bs-lives{display:flex;gap:2px;font-size:1.05rem}
.bs-heart{filter:drop-shadow(0 2px 4px rgba(255,40,80,.45));transition:opacity .3s,transform .3s}
.bs-heart.off{opacity:.18;filter:grayscale(1);transform:scale(.85)}
.bs-level{justify-self:center;text-align:center;background:linear-gradient(180deg,#262a38,#171a24);border:1px solid rgba(255,255,255,.12);border-radius:16px;padding:6px 22px;box-shadow:0 6px 18px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.10);}
.bs-level-eyebrow{display:block;font-size:.58rem;letter-spacing:3px;color:#8d92a3;font-weight:800}
.bs-level-num{display:block;font-size:1.45rem;font-weight:800;line-height:1.05;background:linear-gradient(180deg,#fff,#bfc6ff);-webkit-background-clip:text;background-clip:text;color:transparent}
.bs-clock{justify-self:end;font-weight:800;font-size:.95rem;color:#aeb3c2;font-variant-numeric:tabular-nums}
.bs-clock.low{color:#ff5566;animation:bsPulse 1s infinite}
@keyframes bsPulse{50%{opacity:.45}}
.bs-timer{height:10px;border-radius:99px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.08);overflow:hidden}
.bs-timer i{display:block;height:100%;border-radius:99px;background:linear-gradient(90deg,#36e08a,#ffd34d);transition:width 1s linear,background .4s}
.bs-timer i.low{background:linear-gradient(90deg,#ff7a45,#ff3355);box-shadow:0 0 14px rgba(255,60,90,.55)}
.bs-board{display:flex;flex-wrap:wrap;justify-content:center;align-items:flex-end;gap:18px 12px;padding:30px 8px 18px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:24px;min-height:300px;}
.bs-tube{--ball:42px;--cap:4;width:54px;height:calc(var(--cap)*(var(--ball)+5px)+16px);background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.015));border:3px solid rgba(255,255,255,.38);border-top:none;border-radius:0 0 27px 27px;display:flex;flex-direction:column-reverse;align-items:center;gap:5px;padding:6px 0;cursor:pointer;position:relative;transition:transform .15s ease,border-color .2s,box-shadow .25s;box-shadow:inset 0 -14px 22px rgba(0,0,0,.35);}
.bs-tube:active{transform:scale(.97)}
.bs-rim{position:absolute;top:-3px;left:-3px;right:-3px;height:3px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.5) 25%,rgba(255,255,255,.5) 75%,transparent)}
.bs-tube.sel{border-color:#ffd34d;box-shadow:0 0 0 2px rgba(255,211,77,.35),0 0 26px rgba(255,211,77,.35),inset 0 -14px 22px rgba(0,0,0,.35);}
.bs-tube.done{border-color:rgba(90,255,160,.75);box-shadow:0 0 0 2px rgba(90,255,160,.25),0 0 24px rgba(60,255,150,.30),inset 0 -14px 22px rgba(0,0,0,.35);}
.bs-ball{width:var(--ball);height:var(--ball);border-radius:50%;flex:none;position:relative;box-shadow:inset 0 -9px 13px rgba(0,0,0,.38),0 3px 6px rgba(0,0,0,.45);transition:transform .18s ease;}
.bs-ball::after{content:"";position:absolute;top:12%;left:17%;width:36%;height:27%;border-radius:50%;background:radial-gradient(closest-side,rgba(255,255,255,.95),rgba(255,255,255,0));transform:rotate(-18deg);}
.bs-ball.lift{transform:translateY(calc(-1*var(--ball) - 14px)) scale(1.04);filter:drop-shadow(0 6px 10px rgba(0,0,0,.5))}
.bs-ball.drop{animation:bsDrop .3s cubic-bezier(.3,1.4,.5,1)}
@keyframes bsDrop{0%{transform:translateY(calc(-2.4*var(--ball)))}72%{transform:translateY(4px) scaleY(.92)}100%{transform:translateY(0) scaleY(1)}}
.bs-bottom{display:flex;align-items:center;justify-content:space-between;gap:10px}
.bs-undo{font:inherit;font-weight:800;color:#f2f3f7;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.14);border-radius:14px;padding:10px 18px;cursor:pointer;}
.bs-undo:disabled{opacity:.35;cursor:default}
.bs-meta{display:flex;align-items:center;gap:14px;font-weight:700;font-size:.85rem;color:#8d92a3}
.bs-gt{display:flex;align-items:center;gap:7px;color:#ffd34d}
.bs-gt-coin{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;font-size:.55rem;font-weight:800;color:#3a2c00;background:radial-gradient(circle at 32% 28%,#ffe9a0,#f4b400 70%);box-shadow:inset 0 -3px 5px rgba(140,90,0,.5),0 2px 6px rgba(255,200,40,.35);}
.bs-gt-coin.big{width:46px;height:46px;font-size:.95rem}
.bs-overlay{position:fixed;inset:0;z-index:60;display:flex;align-items:center;justify-content:center;background:rgba(8,9,14,.78);backdrop-filter:blur(4px);padding:20px;overflow:hidden;}
.bs-overlay.red{background:rgba(26,8,12,.82)}
.bs-card{position:relative;width:100%;max-width:360px;text-align:center;background:linear-gradient(180deg,#23252f,#15161d);border:1px solid rgba(255,255,255,.12);border-radius:26px;padding:34px 26px 28px;box-shadow:0 30px 70px rgba(0,0,0,.6);animation:bsPop .4s cubic-bezier(.2,1.4,.4,1);}
@keyframes bsPop{0%{transform:scale(.8) translateY(16px);opacity:0}100%{transform:scale(1) translateY(0);opacity:1}}
.bs-card.lose{border-color:rgba(255,80,100,.35);background:linear-gradient(180deg,#2c1a20,#16121a)}
.bs-card-glow{position:absolute;inset:-1px;border-radius:26px;pointer-events:none;box-shadow:0 0 60px rgba(255,211,77,.22) inset}
.bs-card-eyebrow{font-size:.62rem;letter-spacing:3.5px;font-weight:800;color:#8d92a3;margin:0 0 6px}
.bs-card-eyebrow.red{color:#ff8095}
.bs-card-title{font-size:1.85rem;font-weight:800;margin:0 0 14px}
.bs-card-title.gold{background:linear-gradient(180deg,#fff2c4,#ffd34d 55%,#e8a400);-webkit-background-clip:text;background-clip:text;color:transparent;filter:drop-shadow(0 3px 10px rgba(255,200,60,.35));}
.bs-gt-burst{display:flex;align-items:center;justify-content:center;gap:12px;margin:6px 0 12px}
.bs-gt-amount{font-size:2.1rem;font-weight:800;color:#ffd34d;text-shadow:0 2px 14px rgba(255,200,60,.45)}
.bs-card-sub{color:#aab0c0;font-weight:600;font-size:.9rem;margin:0 0 20px}
.bs-card-row{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
.bs-btn{font:inherit;font-weight:800;font-size:1rem;border:none;border-radius:16px;padding:13px 26px;cursor:pointer;transition:transform .12s,filter .15s;}
.bs-btn:active{transform:scale(.96)}
.bs-btn.gold{color:#3a2c00;background:linear-gradient(180deg,#ffe9a0,#ffce3c);box-shadow:0 8px 22px rgba(255,200,40,.35),inset 0 1px 0 rgba(255,255,255,.7);}
.bs-btn.red{color:#fff;background:linear-gradient(180deg,#ff6478,#e22244);box-shadow:0 8px 22px rgba(255,60,90,.35),inset 0 1px 0 rgba(255,255,255,.25);}
.bs-btn.ghost{color:#f2f3f7;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.16)}
.bs-btn:hover{filter:brightness(1.06)}
.bs-confetti{position:absolute;inset:0;pointer-events:none;overflow:hidden}
.bs-confetti i{position:absolute;top:-20px;display:block;animation:bsFall linear infinite;}
@keyframes bsFall{0%{transform:translateY(-30px) rotate(0deg)}100%{transform:translateY(110vh) rotate(540deg)}}
@media (max-width:430px){.bs-tube{--ball:36px;width:47px}}
@media (max-width:370px){.bs-tube{--ball:31px;width:41px}}
@media (prefers-reduced-motion:reduce){.bs-ball.drop,.bs-confetti i,.bs-clock.low,.bs-card{animation:none!important}.bs-ball,.bs-tube,.bs-timer i{transition:none!important}}
`;